/**
 * DateField — cross-platform date picker.
 *
 * - Web: renders a native HTML5 <input type="date"> (works in every browser,
 *   keyboard-accessible, locale-aware).
 * - iOS / Android: opens @react-native-community/datetimepicker.
 *
 * Value/onChange use ISO date strings (YYYY-MM-DD).
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface DateFieldProps {
  label?: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  minDate?: string;
  maxDate?: string;
}

const safeParse = (s: string): Date => {
  try {
    const d = parseISO(s);
    return isValid(d) ? d : new Date();
  } catch {
    return new Date();
  }
};

const formatDisplay = (s: string): string => {
  try {
    const d = parseISO(s);
    if (!isValid(d)) return s;
    return format(d, 'd MMM yyyy', { locale: fr });
  } catch {
    return s;
  }
};

export default function DateField({ label, value, onChange, minDate, maxDate }: DateFieldProps) {
  const [showNativePicker, setShowNativePicker] = useState(false);

  // ── Web: native HTML5 input ────────────────────────────────────────────────
  if (Platform.OS === 'web') {
    return (
      <View style={styles.wrapper}>
        {!!label && <Text style={styles.label}>{label}</Text>}
        {React.createElement('input', {
          type: 'date',
          value,
          min: minDate,
          max: maxDate,
          onChange: (e: any) => onChange(e.target.value),
          style: {
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.backgroundSecondary,
            color: colors.text,
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
            boxSizing: 'border-box',
          },
        })}
      </View>
    );
  }

  // ── Native (iOS / Android): pressable field that opens DateTimePicker ──────
  // Lazy require so the web bundle never tries to load the native module.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const DateTimePicker = require('@react-native-community/datetimepicker').default;

  return (
    <View style={styles.wrapper}>
      {!!label && <Text style={styles.label}>{label}</Text>}
      <TouchableOpacity
        style={styles.fakeInput}
        onPress={() => setShowNativePicker(true)}
        activeOpacity={0.7}
      >
        <Text style={styles.fakeInputText}>{formatDisplay(value)}</Text>
        <Icon name="calendar-month-outline" size={18} color={colors.textSecondary} />
      </TouchableOpacity>

      {showNativePicker && (
        <DateTimePicker
          value={safeParse(value)}
          mode="date"
          display={Platform.OS === 'ios' ? 'inline' : 'default'}
          minimumDate={minDate ? safeParse(minDate) : undefined}
          maximumDate={maxDate ? safeParse(maxDate) : undefined}
          onChange={(_event: any, date?: Date) => {
            // Android closes the dialog on every event; iOS keeps it open until dismissed.
            if (Platform.OS === 'android') setShowNativePicker(false);
            if (date) {
              onChange(format(date, 'yyyy-MM-dd'));
              if (Platform.OS === 'ios') setShowNativePicker(false);
            }
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  label: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  fakeInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  fakeInputText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    flex: 1,
  },
});
